import Button from '@rio-cloud/rio-uikit/Button';
import ClearableInput from '@rio-cloud/rio-uikit/ClearableInput';
import Collapse from '@rio-cloud/rio-uikit/Collapse';
import { useEffect, useState } from 'react';
import {
    type Control,
    Controller,
    type ControllerRenderProps,
    type FieldArrayWithId,
    type FieldPath,
    type UseFormSetValue,
    type UseFormWatch,
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { CardPreview } from '@/components/CardPreview';
import type { ChargingCardsSchemaType } from '@/components/steps/ChargingCardsStep';
import { trackApplyToAllClick } from '@/features/onboarding/utils/analytics';
import { CHARGING_CARDS_CHARACTER_LIMIT } from '@/lib/constants';

export type ChargingCardsCustomizationProps = {
    fields: FieldArrayWithId<ChargingCardsSchemaType, 'cardsListCustomization'>[];
    control: Control<ChargingCardsSchemaType>;
    watch: UseFormWatch<ChargingCardsSchemaType>;
    setValue: UseFormSetValue<ChargingCardsSchemaType>;
    isOrderNewCard?: boolean;
};

export const ChargingCardsCustomization = ({
    fields,
    control,
    watch,
    setValue,
    isOrderNewCard = false,
}: ChargingCardsCustomizationProps) => {
    const [isOpen, setIsOpen] = useState(() => fields.some(({ line1, line2 }) => line1 || line2));
    const [enableApplyToAll, setEnableApplyToAll] = useState(false);
    const intl = useIntl();

    useEffect(() => {
        const currentFirstLine1 = watch('cardsListCustomization')?.[0]?.line1 ?? '';
        const hasDifferentLine1 = fields.slice(1).some((_, index) => {
            const value = watch(`cardsListCustomization.${index + 1}.line1`);
            return value !== currentFirstLine1;
        });

        setEnableApplyToAll(currentFirstLine1 !== '' && hasDifferentLine1);
    }, [fields.length, watch]);

    const handleToggleOpen = () => setIsOpen(prev => !prev);

    const handleInputChange = (
        newValue: string,
        cardIndex: number,
        lineNumber: number,
        field: ControllerRenderProps<ChargingCardsSchemaType, FieldPath<ChargingCardsSchemaType>>
    ) => {
        setValue(field.name, newValue);

        if (lineNumber !== 1) {
            return;
        }

        const isFirstCard = cardIndex === 0;
        const currentFirstLine1 = watch('cardsListCustomization')?.[0]?.line1 ?? '';

        if (isFirstCard) {
            const isAllEmpty = newValue === '';
            const isDifferent = fields.slice(1).some((_f, i) => {
                const otherLine1 = watch(`cardsListCustomization.${i + 1}.line1`);
                return otherLine1 !== newValue;
            });
            setEnableApplyToAll(!isAllEmpty && isDifferent);
        } else if (newValue !== currentFirstLine1 && currentFirstLine1 !== '') {
            setEnableApplyToAll(true);
        }
    };

    const handleApplyToAllCards = () => {
        const line1 = watch('cardsListCustomization')?.[0]?.line1;
        if (!line1) {
            return;
        }

        fields.slice(1).forEach((_, index) => {
            setValue(`cardsListCustomization.${index + 1}.line1`, line1);
        });

        setEnableApplyToAll(false);

        trackApplyToAllClick(isOrderNewCard, line1);
    };

    const renderCharacterCount = (value: string | undefined) => (
        <span className='text-color-dark text-size-12 padding-y-2 white-space-nowrap'>
            {value?.length || 0} / {CHARGING_CARDS_CHARACTER_LIMIT}
        </span>
    );

    const renderLineInput = (
        index: number,
        lineNumber: number,
        field: ControllerRenderProps<ChargingCardsSchemaType, FieldPath<ChargingCardsSchemaType>>
    ) => {
        const isFirstCardLine1 = index === 0 && lineNumber === 1;
        const fieldValue = typeof field.value === 'string' ? field.value : undefined;
        return (
            <div className='display-flex flex-column'>
                <label className='control-label'>{`${intl.formatMessage({ id: 'onboarding.printedLine' })} ${lineNumber}`}</label>
                <ClearableInput
                    value={fieldValue}
                    onChange={newValue => handleInputChange(newValue, index, lineNumber, field)}
                    maxLength={25}
                />
                <div
                    className={`display-flex align-items-center ${isFirstCardLine1 ? 'justify-content-between' : 'justify-content-end height-35'}`}
                >
                    {isFirstCardLine1 && (
                        <button
                            type='button'
                            className='btn btn-link btn-primary width-auto align-self-start padding-0'
                            onClick={handleApplyToAllCards}
                            disabled={!enableApplyToAll}
                            data-testid='apply-to-all-button'
                        >
                            <span className='uikit-icon icon-duplicate' aria-hidden='true' />
                            <span>{intl.formatMessage({ id: 'onboarding.applyToAllCards' })}</span>
                        </button>
                    )}
                    {renderCharacterCount(fieldValue)}
                </div>
            </div>
        );
    };

    const renderCardInputs = (cardIndex: number) => {
        const watchValues = watch('cardsListCustomization')[cardIndex];

        return (
            <div key={`${cardIndex}-line`} className='width-100pct'>
                <div className='text-bold margin-bottom-10'>{`${intl.formatMessage({ id: 'onboarding.card' })} ${cardIndex + 1}`}</div>
                <div className='display-flex gap-20'>
                    <div className='display-flex flex-column width-100pct'>
                        <Controller
                            name={`cardsListCustomization.${cardIndex}.line1`}
                            control={control}
                            render={({ field }) => renderLineInput(cardIndex, 1, field)}
                        />
                        <Controller
                            name={`cardsListCustomization.${cardIndex}.line2`}
                            control={control}
                            render={({ field }) => renderLineInput(cardIndex, 2, field)}
                        />
                    </div>
                    <hr className='width-1 height-150 bg-gray margin-0 margin-top-15' />
                    <div className='align-self-center'>
                        <CardPreview line1={watchValues.line1} line2={watchValues.line2} />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className='border-color-gray rounded border'>
            <div className='display-flex padding-x-15'>
                <div className='flex-basis-80pct'>
                    <h2 className='text-bold text-size-18'>
                        <FormattedMessage id='onboarding.customiseChargingCards.title' />
                    </h2>
                    <p className='margin-bottom-20'>
                        <FormattedMessage id='onboarding.customiseChargingCards.description' />
                    </p>
                </div>
                <div className='display-flex flex-basis-20pct align-items-center justify-content-end'>
                    <Button onClick={handleToggleOpen} bsStyle='primary' variant='link'>
                        <span className='text-decoration-underline'>
                            <FormattedMessage
                                id={isOpen ? 'onboarding.button.close' : 'onboarding.button.customiseCards'}
                            />
                        </span>
                    </Button>
                </div>
            </div>

            <Collapse open={isOpen}>
                <div className='bg-lighter rounded-bottom padding-15'>
                    <span className='display-flex align-items-center margin-bottom-20'>
                        <i className='uikit-icon icon-info-sign margin-right-5' />
                        <FormattedMessage id='onboarding.warning.previewCards.text' />
                    </span>
                    <div
                        className={`display-grid grid-cols-2-lg grid-cols-1 gap-20 height-100pct ${!isOpen && 'hidden'}`}
                    >
                        {fields?.map((_, index) => renderCardInputs(index))}
                    </div>
                </div>
            </Collapse>
        </div>
    );
};
